import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/** Escape a literal for embedding in a `RegExp`. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Address shape produced by Google Places — every consumer of this component
 * gets the same flattened structure regardless of which address-component
 * types Google chose to return. `latitude`/`longitude` are guaranteed only
 * when `place.geometry` resolved server-side; treat zero as "no geometry".
 */
export interface PlacesAutocompleteResult {
  /** Display name from the place (often a business name); falls back to `address1`. */
  name: string;
  /**
   * Street-level component. When the place has both a subpremise/premise AND
   * a street address, the building parts win and the street goes to `address2`.
   */
  address1: string;
  /** Secondary line — sublocality / overflow from address1. */
  address2: string;
  city: string;
  postcode: string;
  /** ISO 3166-1 alpha-2 (`short_name` from Google). */
  countryIsoCode: string;
  latitude: number;
  longitude: number;
  /**
   * Google's `formatted_address` verbatim — the single-line address the
   * Places / Geocoder API returned. Carries the full text including the
   * bits our structured parse drops (unit numbers, townlands, county
   * names, etc.). Use for display + verbatim storage; the structured
   * fields above stay authoritative for filter / sort queries.
   */
  formattedAddress: string;
  /**
   * Google's stable <c>place_id</c>. Lets us re-fetch fresh data for
   * an address later, and dedupes rows that resolved to the same
   * physical place. Empty string when Google didn't return one (rare).
   */
  placeId: string;
}

/**
 * Tolerant view of "any Google address result" — accepts both
 * <c>google.maps.places.PlaceResult</c> (Autocomplete + Place Details
 * responses) and <c>google.maps.GeocoderResult</c> (reverse-geocode
 * from Geocoder.geocode). They share <c>address_components</c>,
 * <c>geometry.location</c>, and <c>formatted_address</c>; only the
 * Place-side <c>name</c> is missing on Geocoder rows, which we handle
 * by falling back through alternatives in the extractor below.
 */
export interface GoogleAddressResult {
  address_components?: google.maps.GeocoderAddressComponent[];
  geometry?: { location?: google.maps.LatLng };
  /** Only present on PlaceResult, not GeocoderResult. */
  name?: string;
  formatted_address?: string;
  place_id?: string;
}

/**
 * Flatten a Google Places / Geocoder result into the suite's
 * <see cref="PlacesAutocompleteResult"/> shape. Shared between the
 * forward-search path ({@link PlacesAutocomplete}) and the reverse-
 * geocode path (the "Find on map" button on
 * <c>AddressInlineEditor</c>) so both ways of landing on an address
 * produce identical structured fields.
 *
 * <p>
 * Field-derivation rules (worth understanding before changing):
 * <ul>
 *   <li><strong>address1</strong> — prefers subpremise+premise (unit
 *       numbers / building names) when present; otherwise falls back
 *       to <c>street_number + route</c>; otherwise the place's own
 *       name (rural / townland addresses common in IE & UK often
 *       have no street_number).</li>
 *   <li><strong>address2</strong> — secondary line, typically the
 *       sublocality. Gets the street address when subpremise/premise
 *       won the address1 slot.</li>
 *   <li><strong>city</strong> — locality, postal_town, or admin_area
 *       level 1 fallback chain.</li>
 *   <li><strong>countryIsoCode</strong> — short_name from the
 *       country component (ISO 3166-1 alpha-2).</li>
 * </ul>
 * </p>
 */
export function extractAddressFromGoogleResult(
  place: GoogleAddressResult,
): PlacesAutocompleteResult {
  const components = place.address_components ?? [];
  const get = (type: string) =>
    components.find((c) => c.types.includes(type))?.long_name ?? '';
  const getShort = (type: string) =>
    components.find((c) => c.types.includes(type))?.short_name ?? '';

  const subpremise = get('subpremise');
  const premise = get('premise');
  const streetNumber = get('street_number');
  const route = get('route');
  const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
  const sublocality = get('sublocality_level_1');
  const neighborhood = get('neighborhood');

  // City fallback chain — used both for the `city` field and for
  // trimming it out of the place name when we use the name as
  // `address1`.
  const city =
    get('locality') || get('postal_town') || get('administrative_area_level_1');

  const buildingParts = [subpremise, premise].filter(Boolean).join(', ');
  let address1: string;
  let address2: string;
  if (buildingParts) {
    address1 = buildingParts;
    address2 = [streetAddress, sublocality].filter(Boolean).join(', ');
  } else if (streetAddress) {
    address1 = streetAddress;
    address2 = sublocality;
  } else {
    // Rural / townland addresses (common in IE & UK) often return no
    // `street_number` or `route` — Google models the area name as the
    // place's own `name` instead. Fall back through name → neighborhood
    // → sublocality so the field isn't left empty. We strip a trailing
    // ", <city>" if Google folded it into the name to avoid duplication.
    // GeocoderResult has no `name` — formatted_address's leading
    // segment is the next-best stand-in.
    const formattedFirst = place.formatted_address?.split(',')[0]?.trim() ?? '';
    const fallback = place.name ?? neighborhood ?? sublocality ?? formattedFirst ?? '';
    const trimmedFallback = city
      ? fallback.replace(new RegExp(`,\\s*${escapeRegex(city)}$`, 'i'), '')
      : fallback;
    address1 = trimmedFallback;
    address2 = sublocality && sublocality !== trimmedFallback ? sublocality : '';
  }

  const postcode = get('postal_code');
  // ISO country override for Northern Ireland: Google identifies the
  // whole UK as `GB`, but post-Brexit customs treats NI as a separate
  // territory under ISO 3166-1 reserved code `XI` (in the EU VAT area
  // for goods, distinct GB-vs-XI rules on import/export declarations,
  // INTRASTAT, etc.). NI postcodes are the only UK postcodes that
  // start with `BT` — no overlap, no false positives. Detect that
  // pattern and rewrite the country code so downstream features
  // (tax + customs) read the right jurisdiction without operators
  // having to remember to manually pick XI from the dropdown.
  const rawCountry = getShort('country');
  // Pass the region name too: region-level picks (a city / admin area, e.g.
  // from `types: ['(regions)']`) often carry NO postcode, so the BT-prefix
  // rule alone can't flag NI — `administrative_area_level_1` does.
  const adminArea = get('administrative_area_level_1');
  const countryIsoCode = rewriteCountryForNorthernIreland(rawCountry, postcode, adminArea);

  return {
    name: place.name ?? address1 ?? '',
    address1,
    address2,
    city,
    postcode,
    countryIsoCode,
    latitude: place.geometry?.location?.lat() ?? 0,
    longitude: place.geometry?.location?.lng() ?? 0,
    formattedAddress: place.formatted_address ?? '',
    placeId: place.place_id ?? '',
  };
}

/**
 * Override Google's `GB` country code with `XI` (Northern Ireland). NI is
 * detected by EITHER signal:
 *  - a `BT`-prefixed postcode — the only UK postcodes with that prefix, so
 *    unambiguous (covers street-level address picks); or
 *  - an `administrative_area_level_1` of "Northern Ireland" — needed for
 *    region-level picks (cities / admin areas) that carry no postcode.
 * Any other input passes through unchanged.
 *
 * <p>
 * Exported so non-Google address-entry paths (manual postcode edits
 * that change the country's meaning) can apply the same rule —
 * keeping the GB-vs-XI determination in one place rather than
 * scattering NI checks across the codebase.
 * </p>
 */
export function rewriteCountryForNorthernIreland(
  countryIsoCode: string,
  postcode: string | null | undefined,
  adminArea?: string | null,
): string {
  if (countryIsoCode !== 'GB') return countryIsoCode;
  const trimmedPostcode = postcode?.trim().toUpperCase() ?? '';
  if (trimmedPostcode.startsWith('BT')) return 'XI';
  if ((adminArea ?? '').trim().toLowerCase() === 'northern ireland') return 'XI';
  return countryIsoCode;
}

/**
 * Fields every Places lookup must request — Google's Place Details API
 * silently drops anything not listed, so `place_id` (and geometry) come
 * back `undefined` if omitted. Shared by the Autocomplete widget, its
 * Enter-key fallback, and the standalone {@link geocodeText} helper.
 */
const PLACE_DETAIL_FIELDS: Array<keyof google.maps.places.PlaceResult> = [
  'address_components',
  'geometry',
  'name',
  'formatted_address',
  'place_id',
];

/**
 * Resolve a free-text query to a single {@link PlacesAutocompleteResult}
 * using the same two-step path the Enter-key fallback uses:
 * `AutocompleteService.getPlacePredictions` → take the top prediction →
 * `PlacesService.getDetails` → flatten via
 * {@link extractAddressFromGoogleResult}.
 *
 * <p>
 * Exists so non-interactive callers (e.g. the TMS booking form, which
 * geocodes a carrier's port terminal from <c>"{carrier} {port}"</c> text
 * without ever rendering a search box) can reuse the exact resolution
 * logic instead of re-implementing the Google dance. Resolves to
 * <c>null</c> when Places is unavailable, the query is blank, no
 * prediction matches, or the details lookup returns no geometry —
 * callers treat <c>null</c> as "couldn't geocode" and fall back.
 * </p>
 *
 * @param placesLib the library handle from `useMapsLibrary('places')`
 * @param query free-text to resolve (e.g. `"Irish Ferries Dublin Port"`)
 * @param countryIsoCode optional ISO 3166-1 alpha-2 restriction
 */
export function geocodeText(
  placesLib: google.maps.PlacesLibrary | null,
  query: string,
  countryIsoCode?: string,
): Promise<PlacesAutocompleteResult | null> {
  const trimmed = query.trim();
  if (!placesLib || !trimmed) return Promise.resolve(null);

  return new Promise((resolve) => {
    const service = new placesLib.AutocompleteService();
    const request: google.maps.places.AutocompletionRequest = { input: trimmed };
    if (countryIsoCode) {
      request.componentRestrictions = { country: countryIsoCode.toLowerCase() };
    }

    service.getPlacePredictions(request, (predictions) => {
      const top = predictions?.[0];
      if (!top) {
        resolve(null);
        return;
      }
      const placeService = new placesLib.PlacesService(document.createElement('div'));
      placeService.getDetails({ placeId: top.place_id, fields: PLACE_DETAIL_FIELDS }, (place) => {
        if (!place?.geometry) {
          resolve(null);
          return;
        }
        resolve(extractAddressFromGoogleResult(place));
      });
    });
  });
}

export interface PlacesAutocompleteProps {
  /** Fires once the user picks a result and Google resolves geometry. */
  onPlaceSelected: (place: PlacesAutocompleteResult) => void;
  /**
   * Optional controlled value for the input text. When provided, the
   * component reads + emits through this prop instead of its internal
   * state. Lets a parent push an updated formatted address into the
   * search box from outside (e.g. when a reverse-geocode succeeds and
   * we want the search line to reflect the result).
   *
   * When omitted, the component runs uncontrolled with its own
   * internal text state — same as before.
   */
  value?: string;
  /** Pairs with {@link value} when running controlled. */
  onValueChange?: (next: string) => void;
  placeholder?: string;
  /** ISO 3166-1 alpha-2; restricts predictions to this country when set. */
  countryIsoCode?: string;
  /**
   * Google Autocomplete `types` filter. Defaults to street/business
   * addresses (`['geocode', 'establishment']`). Pass `['(regions)']` to
   * suggest cities / postcodes / administrative areas instead — used by
   * lane / zone pickers that price by region rather than a precise point.
   */
  types?: string[];
  /** Floating label rendered above the input (matches `<TextInput>` look). */
  label?: ReactNode;
  /** Helper hint shown below the input. */
  helperText?: ReactNode;
  className?: string;
}

/**
 * Single-line address search backed by Google Places Autocomplete. Lifted
 * from My.Tms.Old/PlacesAutocomplete.tsx and rehomed in `@my/ui` so every
 * app in the suite can drop one in.
 *
 * Requirements:
 *  - Must be rendered inside `<APIProvider apiKey={...}>` from
 *    `@vis.gl/react-google-maps`. The provider is wrapped at the app shell
 *    level — this component just consumes context.
 *  - `useMapsLibrary('places')` triggers the lazy script load on first
 *    mount; subsequent mounts reuse the cached library.
 *
 * UX:
 *  - Typeahead dropdown appears under the input; picking a suggestion
 *    fires `onPlaceSelected` with the flattened result.
 *  - Pressing Enter without picking falls back to the top prediction —
 *    keeps the keyboard-only flow working when the dropdown is dismissed.
 */
export function PlacesAutocomplete({
  onPlaceSelected,
  value: controlledValue,
  onValueChange,
  placeholder = 'Search for an address…',
  countryIsoCode,
  types,
  label,
  helperText,
  className,
}: PlacesAutocompleteProps) {
  const placesLib = useMapsLibrary('places');
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  // Controlled-or-uncontrolled: if the parent passes `value` we treat
  // it as the source of truth and emit changes via `onValueChange`;
  // otherwise fall back to the internal state. Lets the AddressInline
  // Editor sync the search box from reverse-geocode results without
  // breaking the original standalone uncontrolled use.
  const [internalValue, setInternalValue] = useState('');
  const isControlled = controlledValue !== undefined;
  const inputValue = isControlled ? controlledValue : internalValue;
  const setInputValue = (next: string) => {
    if (isControlled) {
      onValueChange?.(next);
    } else {
      setInternalValue(next);
    }
  };
  const reactId = useId();
  const inputId = `places-${reactId}`;

  // ── Bind Google's Autocomplete widget once the library loads. ──────────
  useEffect(() => {
    if (!placesLib || !inputRef.current || autocompleteRef.current) return;

    const options: google.maps.places.AutocompleteOptions = {
      // `place_id` MUST be listed explicitly — Google's Place Details
      // API drops fields that aren't requested, so without it
      // `getPlace().place_id` comes back undefined and the editor
      // can't save (placeId is one of the required fields the
      // FullAddress migration introduced for stable dedup / re-fetch).
      fields: PLACE_DETAIL_FIELDS,
      types: types ?? ['geocode', 'establishment'],
    };

    if (countryIsoCode) {
      options.componentRestrictions = { country: countryIsoCode.toLowerCase() };
    }

    const autocomplete = new placesLib.Autocomplete(inputRef.current, options);

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place.geometry) return;
      const result = extractAddressFromGoogleResult(place);
      onPlaceSelected(result);
      setInputValue(place.formatted_address ?? place.name ?? '');
    });

    autocompleteRef.current = autocomplete;
  }, [placesLib, onPlaceSelected, countryIsoCode, types]);

  // ── Update country restriction when prop changes. ──────────────────────
  useEffect(() => {
    if (!autocompleteRef.current) return;
    autocompleteRef.current.setComponentRestrictions(
      countryIsoCode ? { country: countryIsoCode.toLowerCase() } : { country: [] },
    );
  }, [countryIsoCode]);

  // ── Enter-key fallback: pick the top prediction. ───────────────────────
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !placesLib || !inputValue.trim()) return;
    e.preventDefault();
    void geocodeText(placesLib, inputValue, countryIsoCode).then((result) => {
      if (!result) return;
      onPlaceSelected(result);
      setInputValue(result.formattedAddress || result.name || '');
    });
  };

  return (
    <div className={cn('w-full', className)}>
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        {/*
          Browser-autofill suppression: Chrome (and friends) ignore
          `autocomplete="off"` on inputs that look like address fields and
          will happily drop the user's saved street address on top of the
          Places dropdown. The reliable workaround is `autocomplete="new-password"`
          paired with the password-manager opt-outs (1Password / LastPass)
          so the field doesn't get adopted as a credential either.
        */}
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          autoComplete="new-password"
          data-1p-ignore
          data-lpignore="true"
          data-form-type="other"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder ?? ' '}
          className={cn(
            'peer h-9 w-full rounded-sm border border-slate-300 bg-white px-2 pb-1 pl-7 pt-2 text-sm font-semibold text-slate-900',
            'focus:outline-none focus:ring-2 focus:ring-blue-500',
          )}
        />
        {label && (
          <label
            htmlFor={inputId}
            className="absolute -top-2 left-3 bg-white px-1 text-xs text-slate-500"
          >
            {label}
          </label>
        )}
      </div>
      {helperText && <p className="mt-1 text-xs text-slate-500">{helperText}</p>}
    </div>
  );
}
